import React from 'react'
import { Text, XStack, YStack, styled } from 'tamagui'
import { Metadata } from '@newComponents/Metadata'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'
import { H1, H2, H3, Strong, Paragraph } from '@newComponents/Text'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Image } from '@newComponents/Image'
import { Card } from '@newComponents/Card'
import { ExternalLink, Link } from '@newComponents/Link'
import clickProfile from './images/meetup-click-profile.png'
import selectGroup from './images/meetup-select-group.png'
import selectGuilds from './images/guild-select-guilds.jpg'
import selectSettings from './images/guild-guilds-settings.jpg'
import meetupSettings from './images/guild-meetup-settings.jpg'

const StyledImage = ({ src }: { src: string }) => {
  return (
    <XStack justifyContent='center'>
      <Image
        src={src}
        width='100%'
        maxWidth={500}
        borderWidth={1}
        borderColor='$grey9'
      />
    </XStack>
  )
}

export const MeetupIntegrationScreen = () => {
  return (
    <>
      <Metadata title='Meetup Integration' />

      <GradientBackgroundScreen alignItems='center'>
        <YStack gap='$7'>
          <Section>
            <H1>Meetup Integration</H1>
            <H2>Why Guild?</H2>
            <Paragraph>
              Guild is a platform that has been meticulously crafted with
              communities at the forefront.
            </Paragraph>
            <Paragraph>
              It's completely free! You can organize{' '}
              <Link href='/events'>Events</Link> (in-person/online/both), list{' '}
              <Link href='/presentations'>Presentations</Link>, and join
              Networks.
            </Paragraph>
            <YStack gap='$1'>
              <Paragraph>You can connect your events with others.</Paragraph>
              <Paragraph>
                For example: Are you hosting a JavaScript event in your local
                town? With our Networks features, you can connect with
                organisers in other cities and promote your event across
                multiple places.
              </Paragraph>
            </YStack>
            <Paragraph>
              You can collect Donations and charge for Tickets. Guild charges a
              simple transaction fee of 5% on top of payment processing fees.
            </Paragraph>
          </Section>

          <Section>
            <H2>Features</H2>

            <Paragraph fontWeight='$5'>
              This integration's purpose is to copy events from Meetup to Guild.
            </Paragraph>

            <Paragraph>
              Meetup provides limited tooling for this purpose, and this is the
              only method of data export/integration possible short of an
              expensive Pro plan.
            </Paragraph>

            <Paragraph>
              If you have a Meetup Pro plan and would like to register your
              interest in a tighter integration, let us know via email at{' '}
              <span className='notranslate'>
                <ExternalLink href='mailto:hi@guild.host'>
                  hi@guild.host
                </ExternalLink>
              </span>
              .
            </Paragraph>
          </Section>

          <Section>
            <H2>How to set up the Meetup Integration</H2>
            <H3>Step 1: Retrieve your group on Meetup</H3>
            <Paragraph>
              Sign in to Meetup, then go to <Strong>Your groups</Strong> by
              clicking on your profile picture (or initials) on the right hand
              side of the screen, a drop-down menu will appear, select Your
              groups.
            </Paragraph>

            <StyledImage src={clickProfile} />

            <Paragraph>
              Select the group you want to migrate to Guild.
            </Paragraph>

            <StyledImage src={selectGroup} />

            <Paragraph>Copy the URL, go back to Guild.</Paragraph>
          </Section>

          <Section>
            <H3>Step 2: Add your group to Guild Settings Page</H3>

            <Paragraph>
              To access your events, go to your profile section, by clicking on
              your avatar.
            </Paragraph>
            <Paragraph>
              Click on the Guilds section on the left of your screen, right
              underneath your picture.
            </Paragraph>
            <StyledImage src={selectGuilds} />
            <Paragraph>
              Click on your Guild Your events will appear in the centre of the
              screen, click on the one want to modify.
            </Paragraph>
            <Paragraph>
              Click on the 'settings' button underneath your event’s picture on
              the left.
            </Paragraph>
            <StyledImage src={selectSettings} />
            <Paragraph>
              A new page will open with the event settings section on the left.
            </Paragraph>

            <Paragraph>
              When you’re on the Settings page, click on the ‘Meetup’ section on
              the left hand side of the screen. This section will allow you to
              integrate your Meetup events with Guild.
            </Paragraph>
            <StyledImage src={meetupSettings} />
            <Paragraph>
              Once you’re on the Meetup integration page, you’ll be asked to
              enter your Meetup id.
            </Paragraph>
            <Paragraph>
              Paste in the Meetup URL, then find your meetup group URL, which
              comes after meetup.com. For example, for{' '}
              <Strong>https://www.meetup.com/your-group-id/</Strong>, the URL
              Name would be <Strong>your-group-id</Strong>. Click on the ‘Save’
              button.
            </Paragraph>
            <Paragraph>
              And you’re done! Guild will automatically update the data of any
              upcoming Meetup event you hold with this group, and create the
              same event on Guild.
            </Paragraph>
          </Section>

          <Section>
            <H3>Step 3: Manual updates</H3>
            <YStack gap='$1'>
              <Paragraph>
                There are some information that we can’t retrieve from Meetup,
                so for each event you’ll need to manually update:
              </Paragraph>
              <Text>- Description</Text>
              <Text>- Address Capacity/Attendance Limit</Text>
              <Text>- Virtual URL Event</Text>
              <Text>- Cover Photo</Text>
            </YStack>
          </Section>

          <Section>
            <Paragraph>
              We're always available at{' '}
              <ExternalLink href='mailto:hi@guild.host'>
                hi@guild.host
              </ExternalLink>{' '}
              if you need anything
            </Paragraph>

            <XStack gap='$1'>
              <Paragraph>
                Looking forward to seeing what you get up to!
              </Paragraph>
              <EmojiIcon name='red-heart'></EmojiIcon>
            </XStack>
          </Section>
        </YStack>
      </GradientBackgroundScreen>
    </>
  )
}

export default MeetupIntegrationScreen

const Section = styled(Card, {
  tag: 'section',
  gap: '$6',
  maxWidth: 600,
  width: '100%',
})
